import { DOMOutputSpec, Node as ProsemirrorNode, NodeSpec } from "prosemirror-model";
import {
	generateIndentionStyles,
	IndentionNodeAttributesPart,
	NodeSpecWithIndentionSupport,
	parseIndention
} from "./attributes/indention";
import {
	generateTextAlignStyles,
	NodeSpecWithTextAlignSupport,
	parseTextAlign,
	TextAlignNodeAttributesPart
} from "./attributes/textAlign";
import { combineStyles } from "./attributes/utils";

export type HeadingNodeAttributes =
	& TextAlignNodeAttributesPart
	& IndentionNodeAttributesPart
	& {
		level: HeadingLevel;
	};

export type HeadingNodeSpec =
	& NodeSpec
	& NodeSpecWithTextAlignSupport
	& NodeSpecWithIndentionSupport;

const getAttrs = (level: HeadingLevel) =>
	(node: Node | string): HeadingNodeAttributes => {
		if (!(node instanceof HTMLElement)) {
			throw new Error("Expected HTMLElement at this point");
		}

		const textAlignAttributes: TextAlignNodeAttributesPart = parseTextAlign(node);
		const indentionAttributes: IndentionNodeAttributesPart = parseIndention(node);

		return {
			...textAlignAttributes,
			...indentionAttributes,
			level
		};
	};

export const heading: HeadingNodeSpec = {
	attrs: {
		level: { default: 1 },
		textAlign: { default: undefined },
		indention: { default: undefined }
	},
	content: "inline*",
	group: "block",
	defining: true,
	parseDOM: [
		{ tag: "h1", getAttrs: getAttrs(1) },
		{ tag: "h2", getAttrs: getAttrs(2) },
		{ tag: "h3", getAttrs: getAttrs(3) },
		{ tag: "h4", getAttrs: getAttrs(4) },
		{ tag: "h5", getAttrs: getAttrs(5) },
		{ tag: "h6", getAttrs: getAttrs(6) }
	],
	toDOM(node: ProsemirrorNode): DOMOutputSpec {
		const attrs = node.attrs as HeadingNodeAttributes;
		const tag: string = `h${attrs.level}`;

		const styleValue: string | undefined = combineStyles(
			generateTextAlignStyles(attrs),
			generateIndentionStyles(attrs)
		);

		return [
			tag,
			{
				style: styleValue
			},
			0
		];
	},
	doesSupportTextAlign: true,
	doesSupportIndention: true
};

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;